import { useState, useEffect, useMemo } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Calendar, Clock, ChevronLeft, ChevronRight, Plus, Edit2,
  Trash2, User, AlertCircle, Loader2, Filter, X, Check, List
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';

// Radix Select refuses an item whose value is "": "none" is this sentinel, stored as ''.
const NONE = '__none__';

// API URL
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format date
const formatDate = (date) => {
  return new Intl.DateTimeFormat('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  }).format(date);
};

// Helper to format time
const formatTime = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
};

// Color palette for shifts
const shiftColors = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
];

export default function SchedulePage() {
  const { token } = useOutletContext();
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  
  // State
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  
  // Modal state
  const [showAddShift, setShowAddShift] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    employee_id: '',
    role_id: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    notes: '',
    color: shiftColors[0]
  });
  const [saving, setSaving] = useState(false);
  
  // Filter state
  const [filterEmployee, setFilterEmployee] = useState('all');
  
  // Calculate week dates
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentWeekStart]);
  
  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      // Format dates for API
      const fromDate = currentWeekStart.toISOString().split('T')[0];
      const toDate = new Date(currentWeekStart);
      toDate.setDate(toDate.getDate() + 6);
      const toDateStr = toDate.toISOString().split('T')[0];
      
      const [shiftsRes, employeesRes, rolesRes] = await Promise.all([
        axios.get(`${API}/labor/shifts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { from: fromDate, to: toDateStr }
        }),
        axios.get(`${API}/labor/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/labor/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setShifts(shiftsRes.data.shifts || []);
      setEmployees(employeesRes.data.employees || []);
      setRoles(rolesRes.data.roles || []);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  });

  // Fetch data
  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, currentWeekStart, fetchData]);
  
  // Navigate weeks
  const previousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };
  
  const nextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };
  
  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
  };
  
  // Get shifts for a specific date and employee
  const getShiftsForDateAndEmployee = (date, employeeId) => {
    const dateStr = date.toISOString().split('T')[0];
    return shifts.filter(s => {
      const shiftDate = new Date(s.start_at).toISOString().split('T')[0];
      return shiftDate === dateStr && s.employee_id === employeeId;
    });
  };
  
  // Open add shift modal
  const openAddShift = (date, employeeId) => {
    setShiftForm({
      employee_id: employeeId?.toString() || '',
      role_id: '',
      date: date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '17:00',
      notes: '',
      color: shiftColors[Math.floor(Math.random() * shiftColors.length)]
    });
    setEditingShift(null);
    setShowAddShift(true);
  };
  
  // Open edit shift modal
  const openEditShift = (shift) => {
    const startDate = new Date(shift.start_at);
    const endDate = new Date(shift.end_at);
    
    setShiftForm({
      employee_id: shift.employee_id.toString(),
      role_id: shift.role_id || '',
      date: startDate.toISOString().split('T')[0],
      start_time: startDate.toTimeString().slice(0, 5),
      end_time: endDate.toTimeString().slice(0, 5),
      notes: shift.notes || '',
      color: shift.color || shiftColors[0]
    });
    setEditingShift(shift);
    setShowAddShift(true);
  };
  
  // Save shift
  const handleSaveShift = async () => {
    if (!shiftForm.employee_id || !shiftForm.date || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const startAt = new Date(`${shiftForm.date}T${shiftForm.start_time}:00`).toISOString();
      const endAt = new Date(`${shiftForm.date}T${shiftForm.end_time}:00`).toISOString();
      
      const payload = {
        employee_id: parseInt(shiftForm.employee_id),
        role_id: shiftForm.role_id || null,
        start_at: startAt,
        end_at: endAt,
        notes: shiftForm.notes,
        color: shiftForm.color
      };
      
      if (editingShift) {
        await axios.patch(`${API}/labor/shifts/${editingShift.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Shift updated');
      } else {
        await axios.post(`${API}/labor/shifts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Shift created');
      }
      
      setShowAddShift(false);
      fetchData();
    } catch (error) {
      console.error('Error saving shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete shift
  const handleDeleteShift = async (shiftId) => {
    if (!confirm('Are you sure you want to delete this shift?')) return;
    
    try {
      await axios.delete(`${API}/labor/shifts/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shift deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.error('Failed to delete shift');
    }
  };
  
  // Filtered employees
  const filteredEmployees = useMemo(() => {
    if (filterEmployee === 'all') return employees;
    return employees.filter(e => e.id.toString() === filterEmployee);
  }, [employees, filterEmployee]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  // Mobile List View Component
  const MobileListView = () => {
    const allShiftsForWeek = [];
    weekDates.forEach(date => {
      filteredEmployees.forEach(emp => {
        const dayShifts = getShiftsForDateAndEmployee(date, emp.id);
        dayShifts.forEach(shift => {
          allShiftsForWeek.push({ ...shift, date, employee: emp });
        });
      });
    });
    
    // Group by date
    const groupedByDate = weekDates.map(date => {
      const dateStr = date.toISOString().split('T')[0];
      const isToday = date.toDateString() === new Date().toDateString();
      const shiftsForDay = allShiftsForWeek.filter(s => 
        s.date.toISOString().split('T')[0] === dateStr
      );
      return { date, isToday, shifts: shiftsForDay };
    });

    return (
      <div className="space-y-4">
        {groupedByDate.map(({ date, isToday, shifts }) => (
          <Card key={date.toISOString()} className={cn("bg-card border-border", isToday && "ring-2 ring-primary")}>
            <CardHeader className="p-3 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={cn("text-sm font-medium", isToday && "text-primary")}>
                    {date.toLocaleDateString('en-US', { weekday: 'long' })}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {formatDate(date)}
                  </CardDescription>
                </div>
                {isToday && <Badge variant="outline" className="text-primary border-primary">Today</Badge>}
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {shifts.length === 0 ? (
                <button
                  onClick={() => openAddShift(date)}
                  className="w-full py-4 border border-dashed border-border rounded-lg flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors min-h-[52px]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Shift
                </button>
              ) : (
                <div className="space-y-2">
                  {shifts.map(shift => (
                    <button
                      key={shift.id}
                      onClick={() => openEditShift(shift)}
                      className="w-full p-3 rounded-lg text-white text-left min-h-[52px]"
                      style={{ backgroundColor: shift.color || '#3b82f6' }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                            <User className="h-3 w-3" />
                          </div>
                          <span className="font-medium text-sm truncate">{shift.employee.full_name}</span>
                        </div>
                        <Badge variant="secondary" className="bg-white/20 text-white text-xs flex-shrink-0">
                          {formatTime(shift.start_at)} - {formatTime(shift.end_at)}
                        </Badge>
                      </div>
                      {shift.role_name && (
                        <div className="text-xs opacity-80 mt-1 ml-8">{shift.role_name}</div>
                      )}
                    </button>
                  ))}
                  <button
                    onClick={() => openAddShift(date)}
                    className="w-full py-2 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-sm min-h-[44px]"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="schedule-page">
      {/* Header - Responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-foreground md:text-2xl">Shift Schedule</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage employee work schedules</p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle - mobile only */}
          {isMobile && (
            <div className="flex rounded-lg border border-border overflow-hidden">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none min-h-[40px]"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className="rounded-none min-h-[40px]"
              >
                <Calendar className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="w-[140px] sm:w-[180px] bg-card h-10">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id.toString()}>
                  {emp.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={() => openAddShift()} className="min-h-[40px]">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Shift</span>
          </Button>
        </div>
      </div>

      {/* Week Navigation - Responsive */}
      <Card className="bg-card border-border">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={previousWeek} className="min-w-[40px] min-h-[40px]">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center min-w-0">
              <h2 className="text-sm sm:text-lg font-semibold text-foreground truncate">
                {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
              </h2>
              <Button variant="outline" size="sm" onClick={goToToday} className="min-h-[36px]">
                Today
              </Button>
            </div>
            
            <Button variant="outline" size="sm" onClick={nextWeek} className="min-w-[40px] min-h-[40px]">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid or List View */}
      {isMobile && viewMode === 'list' ? (
        <MobileListView />
      ) : (
        <Card className="bg-card border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-muted/50">
                  <th className="w-40 p-3 text-left text-sm font-medium text-foreground border-r border-border">
                    Employee
                  </th>
                  {weekDates.map((date, i) => {
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <th 
                        key={i} 
                        className={cn(
                          "p-3 text-center text-sm font-medium border-r border-border last:border-r-0",
                          isToday ? "bg-primary/10 text-primary" : "text-foreground"
                        )}
                      >
                        <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        <div className="text-lg">{date.getDate()}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(employee => (
                  <tr key={employee.id} className="border-t border-border">
                    <td className="p-3 border-r border-border">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground text-sm">{employee.full_name}</div>
                          <div className="text-xs text-muted-foreground">{employee.role}</div>
                        </div>
                      </div>
                    </td>
                    {weekDates.map((date, i) => {
                      const dayShifts = getShiftsForDateAndEmployee(date, employee.id);
                      return (
                        <td 
                          key={i} 
                          className="p-2 border-r border-border last:border-r-0 align-top min-h-[80px] h-20"
                        >
                          <div className="space-y-1">
                            {dayShifts.map(shift => (
                              <div
                                key={shift.id}
                                className="p-1.5 rounded text-xs text-white cursor-pointer hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: shift.color || '#3b82f6' }}
                                onClick={() => openEditShift(shift)}
                              >
                                <div className="font-medium">
                                  {formatTime(shift.start_at)} - {formatTime(shift.end_at)}
                                </div>
                                {shift.role_name && (
                                  <div className="opacity-80">{shift.role_name}</div>
                                )}
                              </div>
                            ))}
                            {dayShifts.length === 0 && (
                              <button
                                onClick={() => openAddShift(date, employee.id)}
                                className="w-full h-12 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add/Edit Shift Modal - Use Sheet on mobile */}
      {isMobile ? (
        <Sheet open={showAddShift} onOpenChange={setShowAddShift}>
          <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle>{editingShift ? 'Edit Shift' : 'Add Shift'}</SheetTitle>
            </SheetHeader>
            
            <div className="space-y-4 pb-20">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select value={shiftForm.employee_id} onValueChange={v => setShiftForm({...shiftForm, employee_id: v})}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Role (Optional)</Label>
                <Select value={shiftForm.role_id || NONE} onValueChange={v => setShiftForm({...shiftForm, role_id: v === NONE ? '' : v})}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No specific role</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={shiftForm.date}
                  onChange={e => setShiftForm({...shiftForm, date: e.target.value})}
                  className="h-12 text-base"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={shiftForm.start_time}
                    onChange={e => setShiftForm({...shiftForm, start_time: e.target.value})}
                    className="h-12 text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={shiftForm.end_time}
                    onChange={e => setShiftForm({...shiftForm, end_time: e.target.value})}
                    className="h-12 text-base"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2 flex-wrap">
                  {shiftColors.map(color => (
                    <button
                      key={color}
                      className={cn(
                        "h-10 w-10 rounded-full transition-all min-w-[40px]",
                        shiftForm.color === color ? "ring-2 ring-offset-2 ring-primary" : ""
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => setShiftForm({...shiftForm, color})}
                    />
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={shiftForm.notes}
                  onChange={e => setShiftForm({...shiftForm, notes: e.target.value})}
                  placeholder="Optional notes..."
                  className="h-12 text-base"
                />
              </div>
            </div>

            <SheetFooter className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t flex gap-2">
              {editingShift && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleDeleteShift(editingShift.id);
                    setShowAddShift(false);
                  }}
                  className="flex-1 h-12"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowAddShift(false)} className="flex-1 h-12">
                Cancel
              </Button>
              <Button onClick={handleSaveShift} disabled={saving} className="flex-1 h-12">
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {editingShift ? 'Update' : 'Create'}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={showAddShift} onOpenChange={setShowAddShift}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingShift ? 'Edit Shift' : 'Add Shift'}</DialogTitle>
              <DialogDescription>
                {editingShift ? 'Update shift details' : 'Create a new shift for an employee'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select value={shiftForm.employee_id} onValueChange={v => setShiftForm({...shiftForm, employee_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id.toString()}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Role (Optional)</Label>
                <Select value={shiftForm.role_id || NONE} onValueChange={v => setShiftForm({...shiftForm, role_id: v === NONE ? '' : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No specific role</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={shiftForm.date}
                  onChange={e => setShiftForm({...shiftForm, date: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={shiftForm.start_time}
                    onChange={e => setShiftForm({...shiftForm, start_time: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={shiftForm.end_time}
                    onChange={e => setShiftForm({...shiftForm, end_time: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  {shiftColors.map(color => (
                    <button
                      key={color}
                      className={cn(
                        "h-8 w-8 rounded-full transition-all",
                        shiftForm.color === color ? "ring-2 ring-offset-2 ring-primary" : ""
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => setShiftForm({...shiftForm, color})}
                    />
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={shiftForm.notes}
                  onChange={e => setShiftForm({...shiftForm, notes: e.target.value})}
                  placeholder="Optional notes..."
                />
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              {editingShift && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    handleDeleteShift(editingShift.id);
                    setShowAddShift(false);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowAddShift(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveShift} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {editingShift ? 'Update' : 'Create'} Shift
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
